import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label, sentinel } from '@sentinel/ui';
import { useUser } from '@sentinel/auth-core';
import { z } from 'zod';
import {
  isConsentRequired,
  userCodeSchema,
  type UserCodeInput,
  type VerifyConsentRequired,
} from '../features/device-code/schema';
import { useDeviceVerify, type VerifyError } from '../features/device-code/use-verify';
import { useGrantStandaloneConsent } from '../features/device-code/use-grant-consent';

const search = z.object({ user_code: z.string().optional() });

const scopeListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 0,
  display: 'grid',
  gap: '0.5rem',
  listStyle: 'none',
};
const scopeItemStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 8,
  background: sentinel.surfaceElevated,
  fontSize: 14,
};

type Phase =
  | { kind: 'entry' }
  | { kind: 'consent'; payload: VerifyConsentRequired }
  | { kind: 'success' }
  | { kind: 'denied' };

function statusOf(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function formatDisplay(code: string): string {
  const v = code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (v.length <= 4) return v;
  return `${v.slice(0, 4)}-${v.slice(4, 8)}`;
}

function CodeEntryStep({
  initialValue,
  onResolved,
  onTopError,
}: {
  initialValue: string | undefined;
  onResolved: (next: Phase) => void;
  onTopError: (msg: string | null) => void;
}) {
  const { t } = useTranslation('device-code');
  const verify = useDeviceVerify();
  const form = useForm<UserCodeInput>({
    resolver: zodResolver(userCodeSchema),
    defaultValues: { user_code: initialValue ?? '' },
  });
  const [inlineError, setInlineError] = useState<string | null>(null);

  async function onSubmit(values: UserCodeInput) {
    setInlineError(null);
    onTopError(null);
    try {
      const r = await verify.mutateAsync({ user_code: values.user_code, approve: true });
      if (isConsentRequired(r)) {
        onResolved({ kind: 'consent', payload: r });
        return;
      }
      onResolved({ kind: 'success' });
    } catch (e) {
      const status = statusOf(e as VerifyError);
      if (status === 401) {
        const here = '/device';
        window.location.assign(`/login?return_to=${encodeURIComponent(here)}`);
        return;
      }
      if (status === 400 || status === 404) {
        setInlineError(t('invalid_code'));
        return;
      }
      onTopError(t('generic_error'));
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      style={{ display: 'grid', gap: '0.75rem' }}
      data-testid="device-code-entry-form"
    >
      <FieldRow>
        <Label htmlFor="user_code">{t('code_label')}</Label>
        <Input
          id="user_code"
          type="text"
          autoComplete="off"
          autoFocus
          inputMode="text"
          placeholder="ABCD-EFGH"
          aria-invalid={form.formState.errors.user_code ? 'true' : undefined}
          {...form.register('user_code', {
            onChange: (event) => {
              const target = event.target as HTMLInputElement;
              target.value = formatDisplay(target.value);
            },
          })}
        />
        <FieldError message={inlineError ?? form.formState.errors.user_code?.message} />
      </FieldRow>
      <Button type="submit" disabled={verify.isPending}>
        {t('continue')}
      </Button>
      <p style={{ margin: 0, fontSize: 12, color: sentinel.textMuted, textAlign: 'center' }}>
        {t('expiry_note')}
      </p>
    </form>
  );
}

function ConsentStep({
  payload,
  onResolved,
  onTopError,
}: {
  payload: VerifyConsentRequired;
  onResolved: (next: Phase) => void;
  onTopError: (msg: string | null) => void;
}) {
  const { t } = useTranslation('device-code');
  const verify = useDeviceVerify();
  const grant = useGrantStandaloneConsent();
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null);

  async function onAllow() {
    onTopError(null);
    setSubmitting('allow');
    try {
      await grant.mutateAsync({
        client_id: payload.client_id,
        scopes: payload.requested_scopes,
      });
      const r = await verify.mutateAsync({ user_code: payload.user_code, approve: true });
      if (isConsentRequired(r)) {
        // Should not happen — we just granted consent. Treat as error.
        onTopError(t('generic_error'));
        setSubmitting(null);
        return;
      }
      onResolved({ kind: 'success' });
    } catch (e) {
      setSubmitting(null);
      const status = statusOf(e as VerifyError);
      if (status === 401) {
        window.location.assign(`/login?return_to=${encodeURIComponent('/device')}`);
        return;
      }
      onTopError(t('generic_error'));
    }
  }

  async function onDeny() {
    onTopError(null);
    setSubmitting('deny');
    try {
      await verify.mutateAsync({ user_code: payload.user_code, approve: false });
      onResolved({ kind: 'denied' });
    } catch (e) {
      setSubmitting(null);
      const status = statusOf(e as VerifyError);
      if (status === 401) {
        window.location.assign(`/login?return_to=${encodeURIComponent('/device')}`);
        return;
      }
      onTopError(t('generic_error'));
    }
  }

  const firstLetter = (payload.client_name.charAt(0) || '?').toUpperCase();

  return (
    <section data-testid="device-code-consent" style={{ display: 'grid', gap: '1rem' }}>
      <header style={{ display: 'grid', gap: '0.25rem' }}>
        <h1 style={{ margin: 0 }}>{t('authorize_title', { client: payload.client_name })}</h1>
        <p style={{ margin: 0 }}>{t('authorize_subtitle', { client: payload.client_name })}</p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            display: 'grid',
            placeItems: 'center',
            background: sentinel.surfaceOverlay,
            fontWeight: 700,
            fontSize: 20,
          }}
        >
          {firstLetter}
        </div>
        <div style={{ fontWeight: 600 }}>{payload.client_name}</div>
      </div>

      {payload.requested_scopes.length > 0 ? (
        <ul style={scopeListStyle} data-testid="device-code-scope-list">
          {payload.requested_scopes.map((s) => (
            <li key={s} style={scopeItemStyle}>
              {s}
            </li>
          ))}
        </ul>
      ) : null}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <Button
          type="button"
          variant="secondary"
          onClick={onDeny}
          disabled={submitting !== null}
          data-testid="device-code-deny"
        >
          {t('deny')}
        </Button>
        <Button
          type="button"
          onClick={onAllow}
          disabled={submitting !== null}
          data-testid="device-code-allow"
        >
          {t('allow')}
        </Button>
      </div>
    </section>
  );
}

function ResultStep({ kind }: { kind: 'success' | 'denied' }) {
  const { t } = useTranslation('device-code');
  const title = kind === 'success' ? t('success_title') : t('denied_title');
  const message = kind === 'success' ? t('success_message') : t('denied_message');
  return (
    <section data-testid={`device-code-${kind}`} style={{ display: 'grid', gap: '0.5rem' }}>
      <h1 style={{ margin: 0 }}>{title}</h1>
      <p style={{ margin: 0 }}>{message}</p>
    </section>
  );
}

function DevicePage() {
  const { t } = useTranslation('device-code');
  const { user_code } = useSearch({ from: '/device' });
  // Keep the SessionProvider primed so 401 redirects fire if the cookie is gone.
  useUser();

  const [phase, setPhase] = useState<Phase>({ kind: 'entry' });
  const [topError, setTopError] = useState<string | null>(null);

  // If `?user_code=...` is provided (deep-link from QR code), pre-submit it
  // by passing the value to CodeEntryStep as the initial form value.
  // The user still confirms by clicking Continue.
  const initialCode = useRef<string | undefined>(user_code).current;

  // Reset on stale state (e.g., navigation back to /device).
  useEffect(() => {
    if (phase.kind === 'entry') setTopError(null);
  }, [phase.kind]);

  return (
    <AuthCard
      {...(phase.kind === 'entry' ? { title: t('title'), subtitle: t('subtitle') } : {})}
    >
      {topError ? (
        <Alert type="error" role="alert">
          {topError}
        </Alert>
      ) : null}

      {phase.kind === 'entry' && (
        <CodeEntryStep
          initialValue={initialCode ? formatDisplay(initialCode) : undefined}
          onResolved={setPhase}
          onTopError={setTopError}
        />
      )}
      {phase.kind === 'consent' && (
        <ConsentStep payload={phase.payload} onResolved={setPhase} onTopError={setTopError} />
      )}
      {(phase.kind === 'success' || phase.kind === 'denied') && <ResultStep kind={phase.kind} />}
    </AuthCard>
  );
}

export const Route = createFileRoute('/device')({
  validateSearch: search,
  component: DevicePage,
});
